import { useEffect, useState } from 'react';
import { Icon } from '@iconify-icon/react';
import { ActionIcon, Button, Drawer, Flex } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { getCookie } from 'cookies-next';
import Link from 'next/link';

import { useGetLoggedInUser } from '@/apis/queries/auth.queries';
import { useAssignedBotsList } from '@/hooks/bot-api';
import { CmsPageDataTypes } from '@/types/entities/cms.entities';
import { ACCESS_TOKEN } from '@/utils/constants';
import PageLink from '@/utils/PageLink';

import ChatcoWordmark from '../shared/Logo/ChatcoWordmark';
import Logo from '../shared/Logo/Logo';

const pageList = [
  {
    label: 'How It Works',
    href: `${PageLink.home}#how-it-works`,
  },
  {
    label: 'Use Cases',
    // Point at the dedicated /use-cases page (same destination as the
    // "View Use Cases →" button). The on-page /#use-cases anchor didn't
    // reliably scroll from the home page under the Next.js Pages Router.
    href: PageLink.useCases,
  },
  {
    label: 'Docs',
    href: PageLink.docsHub,
  },
  {
    label: 'FAQ',
    href: `${PageLink.home}#faq`,
  },
  {
    label: 'Pricing',
    href: `${PageLink.home}#pricing-brand`,
  },
  {
    label: 'Contact',
    href: `${PageLink.contactUs}#contact-us`,
  },
];

interface componentProps {
  contentData?: CmsPageDataTypes;
  /** `navy` matches the homepage hero and shows the vector wordmark with its twinkling star. */
  variant?: 'light' | 'dark' | 'navy';
}

const Header: React.FC<componentProps> = ({ contentData, variant = 'light' }) => {
  const [drawerOpened, drawerActions] = useDisclosure();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { data: userData } = useGetLoggedInUser();
  const { data: assignedBots } = useAssignedBotsList();

  const isTeamMember = (userData as any)?.type === 'TEAM_MEMBER';

  // Determine the correct link based on user type
  const dashboardLink =
    isTeamMember && assignedBots && assignedBots.length > 0
      ? PageLink.conversation(assignedBots[0].id)
      : PageLink.dashboard;

  useEffect(() => {
    const token = getCookie(ACCESS_TOKEN);
    setIsAuthenticated(!!token);
  }, []);

  const isNavy = variant === 'navy';
  const isDark = variant === 'dark' || isNavy;
  const surface = isNavy ? 'bg-[#04182e]' : 'bg-[#1f2937]';

  return (
    <div
      className={
        isDark
          ? `sticky top-0 z-50 border-b border-white/10 ${surface} text-center backdrop-blur-md`
          : 'sticky top-0 z-50 border-b border-gray-200 bg-white text-center backdrop-blur-md'
      }>
      <Flex
        className="mx-auto h-16 w-full gap-2 px-4 xl:w-5/6"
        justify="space-between"
        align="center">
        <Flex className="gap-5" align="center">
          <ActionIcon variant="transparent" onClick={drawerActions.toggle} className="md:hidden">
            <Icon
              icon="tabler:menu-2"
              className={isDark ? 'text-3xl text-gray-100' : 'text-3xl text-gray-900'}
            />
          </ActionIcon>

          <Link href={PageLink.home} aria-label="Chat.co home">
            {isNavy ? (
              <ChatcoWordmark height={30} />
            ) : (
              <Logo
                size={130}
                isLanding
                landingUrl={contentData?.settingSection?.imageUrl as string}
              />
            )}
          </Link>
        </Flex>
        <Flex className="hidden gap-4 md:flex xl:gap-8">
          {pageList.map((page) => (
            <Link
              href={page.href}
              key={page.label}
              className={
                isDark
                  ? 'text-sm font-semibold text-gray-300 hover:text-white'
                  : 'text-sm font-semibold text-gray-600 hover:text-gray-900'
              }>
              {page.label}
            </Link>
          ))}
        </Flex>

        <Flex align="center" className="hidden gap-2 py-2 md:flex">
          <Link
            href={PageLink.privacyPage}
            aria-label="How Chat.co keeps your data private"
            className={
              isDark
                ? 'mr-2 inline-flex items-center gap-1.5 rounded-full border border-[#d3a641]/40 bg-white/5 px-3 py-1.5 text-sm font-semibold text-gray-100 transition-colors hover:border-[#d3a641]/70 hover:bg-white/10'
                : 'mr-2 inline-flex items-center gap-1.5 rounded-full border border-[#d3a641]/40 bg-gradient-to-r from-amber-50/60 to-white px-3 py-1.5 text-sm font-semibold text-gray-800 transition-colors hover:border-[#d3a641]/70 hover:bg-amber-50'
            }>
            <Icon icon="tabler:shield-lock" className="text-base text-[#b8862e]" />
            Private
          </Link>
          <div className="flex gap-4">
            {isAuthenticated ? (
              <Button
                size="md"
                variant="transparent"
                component={Link}
                href={dashboardLink}
                className={
                  isDark
                    ? 'w-fit text-sm font-medium text-gray-100 hover:text-white'
                    : 'w-fit text-sm font-medium text-gray-900'
                }>
                Dashboard
              </Button>
            ) : (
              <Button
                size="md"
                variant="transparent"
                component={Link}
                href={PageLink.login}
                className={
                  isDark
                    ? 'w-fit text-sm font-medium text-gray-100 hover:text-white'
                    : 'w-fit text-sm font-medium text-gray-900'
                }>
                Log in
              </Button>
            )}
            <Button
              size="md"
              className={
                isNavy
                  ? 'bg-[#d3a641] text-sm font-semibold text-[#04182e] hover:bg-[#e0b552]'
                  : 'bg-primary-600 text-sm font-medium'
              }
              component={Link}
              href={PageLink.login}>
              Get started
            </Button>
          </div>
        </Flex>
      </Flex>

      <Drawer
        opened={drawerOpened}
        onClose={drawerActions.close}
        withCloseButton={false}
        classNames={{
          body: isDark ? `pt-2 ${surface} text-gray-100` : 'pt-2 bg-white text-gray-900',
          content: isDark ? surface : 'bg-white',
        }}>
        <Flex align="center" justify="space-between">
          <Link href={PageLink.home} className="py-2" aria-label="Chat.co home">
            {isNavy ? (
              <ChatcoWordmark height={28} twinkle={false} />
            ) : (
              <Logo size={130} isLanding />
            )}
          </Link>{' '}
          <ActionIcon className="bg-primary" onClick={drawerActions.close} aria-label="Close menu">
            <Icon icon="tabler:x" className="text-white" />
          </ActionIcon>
        </Flex>
        <Flex direction="column" className="my-6 gap-4">
          {pageList.map((page) => (
            <Link
              href={page.href}
              key={page.label}
              className={
                isDark
                  ? 'text-sm font-semibold text-gray-300 hover:text-white'
                  : 'text-sm font-semibold text-gray-600 hover:text-gray-900'
              }>
              {page.label}
            </Link>
          ))}
          <Link
            href={PageLink.privacyPage}
            aria-label="How Chat.co keeps your data private"
            className={
              isDark
                ? 'inline-flex w-fit items-center gap-1.5 rounded-full border border-[#d3a641]/40 bg-white/5 px-3 py-1.5 text-sm font-semibold text-gray-100'
                : 'inline-flex w-fit items-center gap-1.5 rounded-full border border-[#d3a641]/40 bg-gradient-to-r from-amber-50/60 to-white px-3 py-1.5 text-sm font-semibold text-gray-800'
            }>
            <Icon icon="tabler:shield-lock" className="text-base text-[#b8862e]" />
            Private
          </Link>
        </Flex>

        <div className="flex gap-4">
          {isAuthenticated ? (
            <Button
              size="md"
              variant="outline"
              component={Link}
              href={dashboardLink}
              className={
                isDark
                  ? 'w-full border border-white/20 text-sm font-medium text-gray-100'
                  : 'w-full border border-gray-200 text-sm font-medium text-gray-900'
              }>
              Dashboard
            </Button>
          ) : (
            <Button
              size="md"
              variant="outline"
              component={Link}
              href={PageLink.login}
              className={
                isDark
                  ? 'w-full border border-white/20 text-sm font-medium text-gray-100'
                  : 'w-full border border-gray-200 text-sm font-medium text-gray-900'
              }>
              Login
            </Button>
          )}
          <Button
            size="md"
            className={
              isNavy
                ? 'w-full bg-[#d3a641] text-sm font-semibold text-[#04182e] hover:bg-[#e0b552]'
                : 'w-full bg-primary-600 text-sm font-medium'
            }
            component={Link}
            href={PageLink.login}>
            Get started
          </Button>
        </div>
      </Drawer>
    </div>
  );
};

export default Header;
